import * as React from 'react';
import styled from 'styled-components';
import { moreThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import { copytextbase } from '@adecco/base-app/src/styles/typography';
import Icon from '@adecco/base-app/src/components/atoms/Icon/Icon';
import calcrem from '@adecco/base-app/src/utilities/calcrem/calcrem';
import Container from '@adecco/base-app/src/components/atoms/Container/Container';
import InfoText from '@adecco/base-app/src/components/atoms/InfoText/InfoText';
import { useAppContext } from '@adecco/base-app/src/utilities/appProvider/appProvider';

interface IFooter {
  className?: string;
}

const footerSectionBase = `
  padding: 20px ${calcrem(15)};

  ${moreThan('md')} {
    margin-top: 0;
    margin-right: auto;
    margin-left: auto;
    width: 100%;
  }
`;

const FooterStyled = styled.footer`
  overflow: hidden;

  ${moreThan('md')} {
    padding-top: 0;
  }

  @media print {
    display: none;
  }
`;

const FooterWrapper = styled.div`
  background-color: #ad0f28;

  ${moreThan('xxl')} {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const FooterHeader = styled.section`
  ${footerSectionBase};

  ${moreThan('lg')} {
    display: flex;
    align-items: center;
    width: fit-content;
    margin: 0;
  }
`;

const ContactTitle = styled.p`
  ${footerSectionBase};
  font-family: ${(props) => props.theme.fonts.Copy};
  font-weight: 14px;
  margin: 0;
  padding: 40px 0 20px;
  color: white;

  ${moreThan('lg')} {
    width: fit-content;
    margin: 0 ${(props) => props.theme.margins.contactLinkListWideScreen} 0 0;
    padding: 0;
  }
`;

const Link = styled.a`
  ${copytextbase};
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  margin: 0;
`;

const ContactLinkList = styled.ul`
  ${moreThan('md')} {
    display: flex;
    align-items: center;
  }
`;

const ContactLinkItem = styled.li`
  &:not(:last-child) {
    margin-bottom: ${(props) => props.theme.margins.contactLinkList};
  }

  ${moreThan('md')} {
    &:not(:last-child) {
      margin-bottom: ${calcrem(0)};
      margin-right: ${(props) => props.theme.margins.contactLinkListWideScreen};
    }
  }
`;

const ContactLink = styled(Link)`
  font-family: ${(props) => props.theme.fonts.Copy};
  font-weight: ${(props) => props.theme.fontWeights.link};
  display: flex;
  flex-direction: row;
  align-items: center;
  color: white;
`;

const ContactLinkIcon = styled(Icon)`
  display: inline-flex;
`;

const ContactLinkText = styled.span`
  padding-left: 10px;
  white-space: nowrap;
`;

const MainFooterWrapper = styled.div`
  ${moreThan('lg')} {
    display: flex;
    align-items: center;
  }
`;

const FooterMain = styled.div`
  ${footerSectionBase};

  ${moreThan('lg')} {
    margin: 0;
    width: fit-content;
  }
`;

const PageLinkList = styled.ul`
  margin: 0 ${calcrem(15 * -1)};

  ${moreThan('md')} {
    display: flex;
  }
`;

const PageLinkListItem = styled.li`
  ${moreThan('md')} {
    &:not(:last-child) {
      border-right: 2px solid #dd4f71;
    }
  }

  ${moreThan('lg')} {
    border-right: 2px solid #dd4f71;
  }
`;

const PageLink = styled(Link)`
  display: block;
  font-family: ${(props) => props.theme.fonts.Copy};
  font-weight: ${(props) => props.theme.fontWeights.page};
  margin: 0;
  padding: ${calcrem(7)} ${calcrem(15)};
  color: white;

  ${moreThan('md')} {
    padding-bottom: ${calcrem(5)};
    padding-top: ${calcrem(5)};
  }

  ${moreThan('lg')} {
    padding: 0 ${calcrem(15)};
  }
`;

const PageItem = styled.p<{
  marginBottom?: boolean;
}>`
  display: block;
  font-family: ${(props) => props.theme.fonts.Copy};
  font-weight: ${(props) => props.theme.fontWeights.page};
  margin: 0;
  padding: ${calcrem(7)} ${calcrem(15)};
  margin-bottom: ${(props) => (props.marginBottom ? props.theme.margins.page : 0)};
  color: white;

  ${moreThan('md')} {
    padding-bottom: ${calcrem(5)};
    padding-top: ${calcrem(5)};
  }
`;

const FooterFooter = styled.section`
  ${footerSectionBase};
  margin: 0 ${calcrem(-15)};
  font-size: 14px;
  padding-bottom: 40px;

  ${moreThan('md')} {
    a,
    p {
      padding-left: 0;
    }
  }

  ${moreThan('lg')} {
    margin: 0;
    width: fit-content;
    padding: 0 ${calcrem(15)};
  }
`;

const InfoTextContainer = styled(Container)`
  margin-bottom: 50px;

  ${moreThan('md')} {
    margin-bottom: 82px;
  }
`;

const Footer: React.FunctionComponent<IFooter> = ({ className }) => {
  const { withImprint, t } = useAppContext();
  const genderText = t('general-all-gender');

  return (
    <FooterStyled className={className}>
      {genderText.trim().length > 1 && (
        <InfoTextContainer>
          <InfoText text={genderText} iconName="information-filled" />
        </InfoTextContainer>
      )}
      <FooterWrapper>
        <FooterHeader>
          <ContactTitle>{t('footer-contact-title')}</ContactTitle>
          <ContactLinkList>
            <ContactLinkItem>
              <ContactLink href={t('footer-tel-link')} target="_blank" rel="noopener">
                <ContactLinkIcon name="phone-filled" width={20} fill="white" />
                <ContactLinkText>{t('footer-tel-text')}</ContactLinkText>
              </ContactLink>
            </ContactLinkItem>
            <ContactLinkItem>
              <ContactLink href={t('footer-mail-link')} target="_blank" rel="noopener">
                <ContactLinkIcon name="email-filled" width={27} fill="white" />
                <ContactLinkText>{t('footer-mail')}</ContactLinkText>
              </ContactLink>
            </ContactLinkItem>
            <ContactLinkItem>
              <ContactLink href={t('footer-branches-link')} target="_blank" rel="noopener">
                <ContactLinkIcon name="pin-filled" width={17} fill="white" />
                <ContactLinkText>{t('footer-branches')}</ContactLinkText>
              </ContactLink>
            </ContactLinkItem>
          </ContactLinkList>
        </FooterHeader>
        <MainFooterWrapper>
          <FooterMain role="navigation" aria-label="Secondary">
            <PageLinkList>
              {withImprint && (
                <PageLinkListItem>
                  <PageLink href={t('footer-imprint-link')} target="_blank">
                    {t('footer-imprint')}
                  </PageLink>
                </PageLinkListItem>
              )}
              <PageLinkListItem>
                <PageLink href={t('footer-data-policy-link')} target="_blank">
                  {t('footer-data-policy')}
                </PageLink>
              </PageLinkListItem>
              <PageLinkListItem>
                <PageLink href={t('footer-cookie-policy-link')} target="_blank">
                  {t('footer-cookie-policy')}
                </PageLink>
              </PageLinkListItem>
              <PageLinkListItem>
                <PageLink href={t('footer-terms-of-use-link')} target="_blank">
                  {t('footer-terms-of-use')}
                </PageLink>
              </PageLinkListItem>
            </PageLinkList>
          </FooterMain>
          <FooterFooter>
            <PageItem>© {t('footer-rights-reserved')}</PageItem>
          </FooterFooter>
        </MainFooterWrapper>
      </FooterWrapper>
    </FooterStyled>
  );
};

export default Footer;
